import type {
  Actual,
  CalibratedProfile,
  Estimate,
  FathomStore,
  Intake,
  ModelPricing,
  Project,
  Reconciliation,
  VelocityBenchmark,
  VelocitySnapshot,
} from "./types.js";

// ── Convex client types (imported dynamically) ────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConvexClient = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConvexApi = any;

/**
 * Dynamically load the convex package.
 * Returns null if the convex package is not installed (it's an optional peer dep).
 */
async function loadConvex(): Promise<{
  ConvexHttpClient: new (url: string) => ConvexClient;
  anyApi: ConvexApi;
} | null> {
  try {
    const [browser, server] = await Promise.all([
      import("convex/browser"),
      import("convex/server"),
    ]);
    return {
      ConvexHttpClient: browser.ConvexHttpClient,
      anyApi: server.anyApi,
    };
  } catch {
    return null;
  }
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * Convert a Convex document (with _id and _creationTime) to our store type
 * by mapping _id to id and stripping Convex internals.
 */
function docToRecord<T extends { id: string }>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doc: Record<string, any>,
): T {
  const { _id, _creationTime, ...rest } = doc;
  return { id: String(_id), ...rest } as unknown as T;
}

// ── ConvexStore ─────────────────────────────────────────────

export class ConvexStore implements FathomStore {
  private url: string;
  private client: ConvexClient | null = null;
  private api: ConvexApi | null = null;
  private initPromise: Promise<boolean> | null = null;

  constructor(config: { url: string }) {
    this.url = config.url;
  }

  /**
   * Lazily initialize the Convex client.
   * Returns true if the client is ready, false if convex is unavailable.
   */
  private async init(): Promise<boolean> {
    if (this.client) return true;

    if (!this.initPromise) {
      this.initPromise = (async () => {
        const convex = await loadConvex();
        if (!convex) return false;

        try {
          this.client = new convex.ConvexHttpClient(this.url);
          this.api = convex.anyApi;
          return true;
        } catch (err) {
          console.error(`[FathomStore] Convex client init failed:`, err);
          return false;
        }
      })();
    }

    return this.initPromise;
  }

  /**
   * Safely execute a Convex query. Returns null on any error.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async safeQuery<T>(fn: any, args: Record<string, unknown>): Promise<T | null> {
    if (!(await this.init())) return null;
    try {
      return await this.client.query(fn, args);
    } catch (err) {
      console.error(`[FathomStore] query failed:`, err);
      return null;
    }
  }

  /**
   * Safely execute a Convex mutation. Returns null on any error.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async safeMutation<T>(fn: any, args: Record<string, unknown>): Promise<T | null> {
    if (!(await this.init())) return null;
    try {
      return await this.client.mutation(fn, args);
    } catch (err) {
      console.error(`[FathomStore] mutation failed:`, err);
      return null;
    }
  }

  // ── Projects ────────────────────────────────────────────

  async getProject(name: string): Promise<Project | null> {
    const doc = await this.safeQuery(this.api?.projects.getByName, { name });
    if (!doc) return null;
    return docToRecord<Project>(doc as Record<string, unknown>);
  }

  async upsertProject(project: Omit<Project, "id">): Promise<string> {
    // Try to find existing
    const existing = await this.safeQuery(this.api?.projects.getByName, {
      name: project.name,
    });
    if (existing) {
      return String((existing as Record<string, unknown>)._id);
    }

    // Create new
    const id = await this.safeMutation(this.api?.projects.create, {
      name: project.name,
      client: project.client,
      claudeCodePath: project.claudeCodePath,
    });
    return id ? String(id) : "";
  }

  // ── Estimates ───────────────────────────────────────────

  async saveEstimate(estimate: Omit<Estimate, "id">): Promise<string> {
    const id = await this.safeMutation(this.api?.estimates.create, {
      projectId: estimate.projectId,
      specHash: estimate.specHash,
      features: estimate.features,
      totalEstimate: estimate.totalEstimate,
      calibrationSource: estimate.calibrationSource,
    });
    return id ? String(id) : "";
  }

  async getEstimates(projectId: string): Promise<Estimate[]> {
    // Convex getByProject returns only the latest; we use it and wrap in array
    // since that's what the existing Convex API exposes
    const doc = await this.safeQuery(this.api?.estimates.getByProject, {
      projectId,
    });
    if (!doc) return [];
    return [docToRecord<Estimate>(doc as Record<string, unknown>)];
  }

  // ── Actuals ─────────────────────────────────────────────

  async saveActual(actual: Omit<Actual, "id">): Promise<string> {
    const id = await this.safeMutation(this.api?.actuals.record, {
      projectId: actual.projectId,
      sessionId: actual.sessionId,
      provider: actual.provider,
      model: actual.model,
      inputTokens: actual.inputTokens,
      outputTokens: actual.outputTokens,
      cacheReadTokens: actual.cacheReadTokens,
      cacheCreateTokens: actual.cacheCreateTokens,
      toolCalls: actual.toolCalls,
      filesTouched: actual.filesTouched,
      estimatedCost: actual.estimatedCost,
      sessionStart: actual.sessionStart,
      sessionEnd: actual.sessionEnd,
      activeMinutes: actual.activeMinutes,
      featureId: actual.featureId,
      tagConfidence: actual.tagConfidence,
      tagSource: actual.tagSource,
      tagStatus: actual.tagStatus,
    });
    return id ? String(id) : "";
  }

  async getActuals(
    projectId: string,
    opts?: { featureId?: string },
  ): Promise<Actual[]> {
    let docs;
    if (opts?.featureId) {
      docs = await this.safeQuery(this.api?.actuals.listByFeature, {
        projectId,
        featureId: opts.featureId,
      });
    } else {
      docs = await this.safeQuery(this.api?.actuals.listByProject, {
        projectId,
      });
    }
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) => docToRecord<Actual>(d));
  }

  // ── Intakes ─────────────────────────────────────────────

  async saveIntake(intake: Omit<Intake, "id">): Promise<string> {
    const id = await this.safeMutation(this.api?.intakes.create, {
      projectId: intake.projectId,
      rawInput: intake.rawInput,
      source: intake.source,
      extractedSlices: intake.extractedSlices,
    });
    return id ? String(id) : "";
  }

  async getIntakes(projectId: string): Promise<Intake[]> {
    const docs = await this.safeQuery(this.api?.intakes.listByProject, {
      projectId,
    });
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) => docToRecord<Intake>(d));
  }

  // ── Velocity Benchmarks ─────────────────────────────────

  async saveBenchmark(
    benchmark: Omit<VelocityBenchmark, "id">,
  ): Promise<string> {
    const id = await this.safeMutation(this.api?.velocity.upsertBenchmark, {
      category: benchmark.category,
      complexity: benchmark.complexity,
      taskType: benchmark.taskType,
      avgTokens: benchmark.avgTokens,
      avgCost: benchmark.avgCost,
      avgSessions: benchmark.avgSessions,
      avgWallClockDays: benchmark.avgWallClockDays,
      avgActiveMinutes: benchmark.avgActiveMinutes,
      p25Days: benchmark.p25Days,
      p75Days: benchmark.p75Days,
      dataPoints: benchmark.dataPoints,
      confidence: benchmark.confidence,
    });
    return id ? String(id) : "";
  }

  async getBenchmarks(opts?: {
    category?: string;
    complexity?: string;
  }): Promise<VelocityBenchmark[]> {
    const docs = await this.safeQuery(this.api?.velocity.queryBenchmarks, {
      category: opts?.category,
      complexity: opts?.complexity,
    });
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) =>
      docToRecord<VelocityBenchmark>(d),
    );
  }

  // ── Velocity Snapshots ──────────────────────────────────

  async saveSnapshot(
    snapshot: Omit<VelocitySnapshot, "id">,
  ): Promise<string> {
    const id = await this.safeMutation(this.api?.velocity.recordSnapshot, {
      projectId: snapshot.projectId || undefined,
      period: snapshot.period,
      featuresCompleted: snapshot.featuresCompleted,
      totalTokens: snapshot.totalTokens,
      totalCost: snapshot.totalCost,
      avgEstimateAccuracy: snapshot.avgEstimateAccuracy,
      avgCostPerFeature: snapshot.avgCostPerFeature,
      avgDaysPerFeature: snapshot.avgDaysPerFeature,
      cacheHitRate: snapshot.cacheHitRate,
      autoTagRate: snapshot.autoTagRate,
    });
    return id ? String(id) : "";
  }

  async getSnapshots(opts?: {
    projectId?: string;
    period?: string;
  }): Promise<VelocitySnapshot[]> {
    const docs = await this.safeQuery(this.api?.velocity.querySnapshots, {
      projectId: opts?.projectId || undefined,
    });
    if (!docs || !Array.isArray(docs)) return [];
    let results = docs.map((d: Record<string, unknown>) =>
      docToRecord<VelocitySnapshot>(d),
    );
    // Client-side period filter (Convex query doesn't filter by period)
    if (opts?.period) {
      results = results.filter((s) => s.period === opts.period);
    }
    return results;
  }

  // ── Reconciliations ─────────────────────────────────────

  async saveReconciliation(
    recon: Omit<Reconciliation, "id">,
  ): Promise<string> {
    const id = await this.safeMutation(this.api?.reconciliations.create, {
      projectId: recon.projectId,
      estimateId: recon.estimateId,
      featureDeltas: recon.featureDeltas,
      overallAccuracy: recon.overallAccuracy,
      autoTagAccuracy: recon.autoTagAccuracy,
      registryImprovements: recon.registryImprovements,
      suggestedAdjustments: recon.suggestedAdjustments,
    });
    return id ? String(id) : "";
  }

  async getReconciliations(projectId: string): Promise<Reconciliation[]> {
    const docs = await this.safeQuery(
      this.api?.reconciliations.listByProject,
      { projectId },
    );
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) =>
      docToRecord<Reconciliation>(d),
    );
  }

  // ── Calibrated Profiles ─────────────────────────────────

  async saveProfile(
    profile: Omit<CalibratedProfile, "id">,
  ): Promise<string> {
    const id = await this.safeMutation(this.api?.calibratedProfiles.upsert, {
      taskType: profile.taskType,
      baseInput: profile.baseInput,
      baseOutput: profile.baseOutput,
      overheadMultipliers: profile.overheadMultipliers,
      dataPoints: profile.dataPoints,
      confidence: profile.confidence,
    });
    return id ? String(id) : "";
  }

  async getProfiles(): Promise<CalibratedProfile[]> {
    const docs = await this.safeQuery(
      this.api?.calibratedProfiles.listAll,
      {},
    );
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) =>
      docToRecord<CalibratedProfile>(d),
    );
  }

  async getProfile(taskType: string): Promise<CalibratedProfile | null> {
    const doc = await this.safeQuery(
      this.api?.calibratedProfiles.getByTaskType,
      { taskType },
    );
    if (!doc) return null;
    return docToRecord<CalibratedProfile>(doc as Record<string, unknown>);
  }

  // ── Model Pricing ───────────────────────────────────────

  async savePricing(pricing: Omit<ModelPricing, "id">): Promise<string> {
    const id = await this.safeMutation(this.api?.modelPricing.upsert, {
      model: pricing.model,
      provider: pricing.provider,
      inputPerMTok: pricing.inputPerMTok,
      outputPerMTok: pricing.outputPerMTok,
      cacheReadPerMTok: pricing.cacheReadPerMTok,
      batchDiscount: pricing.batchDiscount,
    });
    return id ? String(id) : "";
  }

  async getPricing(model?: string): Promise<ModelPricing[]> {
    if (model) {
      const doc = await this.safeQuery(this.api?.modelPricing.getByModel, {
        model,
      });
      if (!doc) return [];
      return [docToRecord<ModelPricing>(doc as Record<string, unknown>)];
    }

    const docs = await this.safeQuery(this.api?.modelPricing.list, {});
    if (!docs || !Array.isArray(docs)) return [];
    return docs.map((d: Record<string, unknown>) =>
      docToRecord<ModelPricing>(d),
    );
  }
}
